import { Icon } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import { inverseCoordinates as inverse } from './geo';
import { Activity, CategoryAttributeLevelSelected, CategoryResponse, CategoryType, EnhancedActivity, isEnhancedActivity } from '../types';

export const flipActivityCoordinates = (activity: Activity) => ({
  ...activity,
  inversedLocation: {
    ...activity.location,
    coordinates: inverse(activity.location?.coordinates),
  },
});

export const getActivitySeasonYear = ({ permanent }: { permanent: boolean }, scoutedAt: string): string =>
  permanent ?
    'seasonAgnostic' :
    scoutedAt.split('-')[0];

export const categoryTypeToText = (observationType: CategoryType) => ({
  DISEASE: getMessage('enhancedScouting.form.category.disease'),
  INSECT: getMessage('enhancedScouting.form.category.insect'),
  WEED: getMessage('enhancedScouting.form.category.weed'),
  NUTRIENT: getMessage('enhancedScouting.form.category.nutrient'),
  WATER: getMessage('enhancedScouting.form.category.water'),
  OTHER: getMessage('enhancedScouting.form.category.other'),
}[observationType])

export const categoryTypeColor = (observationType: CategoryType | null) => ({
  DISEASE: '#018D37',
  INSECT: '#DC1480',
  WEED: '#FF753A',
  NUTRIENT: '#555',
  WATER: '#0BF',
  OTHER: '#153063',
}[observationType ?? '']);

export const categoryTypePinIcon = (observationType: CategoryType | null) => observationType ? ({
  DISEASE: Icon.set.PIN_DISEASE,
  INSECT: Icon.set.PIN_INSECT,
  WEED: Icon.set.PIN_WEEDS,
  NUTRIENT: Icon.set.PIN_NUTRIENTS,
  WATER: Icon.set.PIN_WATER,
  OTHER: Icon.set.PIN_OTHER,
}[observationType]) : Icon.set.PIN_OTHER;

/**
 * Assets in the Enhanced Scouting form can be of type ActivityAsset or File
 * ActivityAsset when an asset is an entity existing from Tenderfoot
 * File when is a new image that wants to be uploaded from user's browser.
 * This function allows us to differ between one or the other, and isolate the type to be File when this evaluates to true
 * */
export const isFile = (input: unknown): input is File => 'File' in window && input instanceof File;

export const toForm = (acc: CategoryAttributeLevelSelected, v: CategoryResponse) => {
  acc[v.nameCode] = v.id;
  return acc;
}

export const getInitialValues = (activity?: EnhancedActivity | Activity) => ({
  title: activity?.title ?? getMessage('enhancedScouting.form.defaultTitle'),
  note:
    activity?.comments && activity.comments.length > 0 && activity.comments
      ? activity.comments[0].note
      : '',
  permanent: activity?.permanent ?? false,
  date: activity?.scoutedAt,
  soilSample: isEnhancedActivity(activity) && activity.soilSample,
  categoryTypeSelected: isEnhancedActivity(activity) ? activity.categoryType : null,
  attributesSelected: isEnhancedActivity(activity)
    ? activity.categoryAttributeItems.reduce<CategoryAttributeLevelSelected>(toForm, {})
    : {},
  assets: activity?.assets ?? []
});

export interface ValidationResult {
  validFiles: File[];
  invalidFileMessage: string | null;
}

const MAX_IMAGES = 10; // Maximum number of images
const MAX_FILE_SIZE_MB = 10; // Maximum file size in MB

export const validateFiles = (
  files: File[],
  selectedFilesCount: number,
  previewsCount: number,
): ValidationResult => {
  const validFiles: File[] = [];
  let invalidFileMessage: string | null = null;

  const hasInvalidFile = files.some((file) => {
    if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
      invalidFileMessage = getMessage('enhancedScouting.form.warningModal.imagesMaxSizeExceed', {
        fileName: file.name,
        maxFileSize: MAX_FILE_SIZE_MB,
      });
      return true;
    }

    if (
      selectedFilesCount + validFiles.length >= MAX_IMAGES ||
      selectedFilesCount + validFiles.length + previewsCount >= MAX_IMAGES
    ) {
      invalidFileMessage = getMessage('enhancedScouting.form.warningModal.imagesMaxAmountExceed', {
        maxImages: MAX_IMAGES,
      });
      return true;
    }

    validFiles.push(file);
    return false;
  });

  if (hasInvalidFile) {
    return {
      validFiles: [],
      invalidFileMessage,
    };
  }

  return {
    validFiles,
    invalidFileMessage: null,
  };
};